import React, { useState, useRef, useMemo, useEffect } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import TestCategoryModal from "../components/modal/TestCategoryModal";
import API from "../api";
import toastr from "toastr";

interface Department {
  id: number;
  dept_name: string;
}

interface TestCategory {
  id?: number;
  dept_id: number;
  dept_name?: string;
  type: string;
  category_name: string;
  description: string;
  amount: string | number;
  created?: string;
  modified?: string;
}

const TestCategories: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<TestCategory | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [departments, setDepartments] = useState<Department[]>([]);
  const tableRef = useRef<any>(null);

  // ✅ Fetch departments from correct endpoint
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await API.get("/testcategories/getDepartments");
        setDepartments(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch departments", err);
      }
    };
    fetchDepts();
  }, []);

  const fetchCategories = async (
    page: number,
    search: string,
    sortBy = "category_name",
    sortOrder = "desc"
  ) => {
    try {
      const res = await API.get(
        `/testcategories/getCategories?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      return { data: res.data.data, total: res.data.total };
    } catch {
      toastr.error("Failed to fetch test categories");
      return { data: [], total: 0 };
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setSelectedCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category: TestCategory) => {
    setModalMode("edit");
    setSelectedCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this test category?")) return;
    try {
      await API.delete(`/testcategories/deleteCategory/${id}`);
      toastr.success("Test category deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete test category");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedCategory(null);
  };

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Department", accessorKey: "dept_name" },
      { header: "Type", accessorKey: "type" },
      { header: "Category Name", accessorKey: "category_name" },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: ({ row }: any) =>
          `₹${parseFloat(row.original.amount || 0).toFixed(2)}`,
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }: any) => (
          <span className="text-truncate d-inline-block" style={{ maxWidth: 200 }}>
            {row.original.description || "-"}
          </span>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: TestCategory = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-light"
                onClick={() => handleEdit(data)}
                title="Edit"
              >
                <i className="fa fa-edit" />
              </button>
              <button
                className="btn btn-light"
                onClick={() => handleDelete(data.id!)}
                title="Delete"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Test Category" />
        <button className="btn btn-light px-5" onClick={handleAdd}>
          <i className="fa fa-plus" /> Create New
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchCategories}
                queryKey={["testcategories"]}
              />
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <TestCategoryModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          categoryData={selectedCategory}
          departments={departments}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default TestCategories;